import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { EmailRecipientsModal } from './EmailRecipientsModal';
import { 
  Download, 
  FileText, 
  CheckSquare, 
  Square,
  MessageSquare,
  Mail,
  Link,
  FileCheck
} from 'lucide-react';

interface ExportSection {
  id: string;
  label: string;
  description?: string;
}

interface ExportOptionsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  exportType: 'pdf' | 'whatsapp' | 'email' | 'link';
  onExport: (selectedSections: string[], recipients?: string[]) => void;
  reportDefaults?: any; // Add optional prop for filtering sections
  obraData?: {
    responsaveis_obra?: string[];
    clientes_responsaveis?: string[];
  };
}

const exportSections: ExportSection[] = [
  { id: 'header', label: 'Cabeçalho', description: 'Dados da obra, cliente, responsável' },
  { id: 'weather', label: 'Condições Climáticas', description: 'Tempo, temperatura' },
  { id: 'workforce', label: 'Mão de Obra', description: 'Funcionários presentes' },
  { id: 'activities', label: 'Atividades', description: 'Atividades executadas' },
  { id: 'equipment', label: 'Equipamentos', description: 'Equipamentos utilizados' },
  { id: 'occurrences', label: 'Ocorrências', description: 'Incidentes e observações' },
  { id: 'materials_received', label: 'Materiais Recebidos', description: 'Materiais entregues' },
  { id: 'materials_used', label: 'Materiais Utilizados', description: 'Materiais consumidos' },
  { id: 'comments', label: 'Comentários (Timeline)', description: 'Histórico de comentários' },
  { id: 'photos', label: 'Fotos', description: 'Galeria de fotos' },
  { id: 'videos', label: 'Vídeos', description: 'Galeria de vídeos' },
  { id: 'attachments', label: 'Anexos', description: 'Documentos anexos' },
  { id: 'signatures', label: 'Assinaturas', description: 'Assinaturas digitais' }
];

const getExportTypeIcon = (type: string) => {
  switch (type) {
    case 'pdf':
      return <FileText className="w-5 h-5" />;
    case 'whatsapp':
      return <MessageSquare className="w-5 h-5" />;
    case 'email':
      return <Mail className="w-5 h-5" />;
    case 'link':
      return <Link className="w-5 h-5" />;
    default:
      return <Download className="w-5 h-5" />;
  }
};

const getExportTypeLabel = (type: string) => {
  switch (type) {
    case 'pdf':
      return 'Exportar PDF';
    case 'whatsapp':
      return 'Enviar WhatsApp';
    case 'email':
      return 'Enviar Email';
    case 'link':
      return 'Gerar Link';
    default:
      return 'Exportar';
  }
};

export const ExportOptionsModal: React.FC<ExportOptionsModalProps> = ({
  open,
  onOpenChange,
  exportType,
  onExport,
  obraData
}) => {
  const [selectedSections, setSelectedSections] = useState<string[]>(
    exportSections.map(section => section.id)
  );
  const [showEmailModal, setShowEmailModal] = useState(false);

  const handleSectionToggle = (sectionId: string) => {
    setSelectedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleSelectAll = () => {
    setSelectedSections(exportSections.map(section => section.id));
  };

  const handleClearAll = () => {
    setSelectedSections([]);
  };

  const handleExport = () => {
    if (exportType === 'email') {
      // For email, show recipients modal first
      setShowEmailModal(true);
    } else {
      // For other types, export directly
      onExport(selectedSections);
      onOpenChange(false);
    }
  };

  const handleEmailSend = (recipients: string[]) => {
    onExport(selectedSections, recipients);
    setShowEmailModal(false);
    onOpenChange(false);
  };

  const allSelected = selectedSections.length === exportSections.length;
  const noneSelected = selectedSections.length === 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {getExportTypeIcon(exportType)}
              Selecionar conteúdo para exportar
              <Button
                size="sm"
                onClick={handleExport}
                disabled={noneSelected}
                className="h-8 px-3 text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300 hover:bg-blue-50 ml-auto mr-8"
                variant="outline"
              >
                {getExportTypeIcon(exportType)}
              </Button>
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Escolha quais seções do relatório deseja incluir na exportação.
            </p>
          </DialogHeader>

          <div className="space-y-4">
            {/* Select All / Clear All buttons */}
            <div className="flex items-center gap-2 pb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={allSelected}
                className="flex items-center gap-2"
              >
                <CheckSquare className="w-4 h-4" />
                Selecionar tudo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                disabled={noneSelected}
                className="flex items-center gap-2"
              >
                <Square className="w-4 h-4" />
                Limpar seleção
              </Button>
              
              <div className="ml-auto text-sm text-muted-foreground">
                {selectedSections.length} de {exportSections.length} selecionados
              </div>
            </div>

            <Separator />

            {/* Sections list */}
            <div className="space-y-3">
              {exportSections.map((section) => (
                <div
                  key={section.id}
                  className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors"
                >
                  <Checkbox
                    id={section.id}
                    checked={selectedSections.includes(section.id)}
                    onCheckedChange={() => handleSectionToggle(section.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={section.id}
                      className="font-medium cursor-pointer block"
                    >
                      {section.label}
                    </Label>
                    {section.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {section.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {noneSelected && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-amber-700">
                  <FileCheck className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Nenhuma seção selecionada
                  </span>
                </div>
                <p className="text-sm text-amber-600 mt-1">
                  Selecione pelo menos uma seção para continuar com a exportação.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleExport}
              disabled={noneSelected}
              className="min-w-[140px]"
            >
              {getExportTypeIcon(exportType)}
              <span className="ml-2">{getExportTypeLabel(exportType)}</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailRecipientsModal
        open={showEmailModal}
        onOpenChange={setShowEmailModal}
        onSendEmail={handleEmailSend}
        obraData={obraData}
      />
    </>
  );
};